import React from 'react';
import config from 'src/shared/config';
import { T } from 'src/shared/helpers';
import { ServiceConfig } from './serviceCredits';

// Centralized configuration for the Test Orders add-on rendered through the shared ServiceCredits
// component. Keeping the add-on copy (name, description, guide link, unit labels) in a single place keeps
// the subscriptions page card and the standalone purchase dialog in sync, and shares the runtime
// availability logic (trial / Shopify test store) instead of duplicating it per call site.

// Runtime context needed to decide whether purchasing for an add-on is currently allowed.
export interface AddonAvailabilityContext {
    // The seller is in a trial period.
    inTrial: boolean;
    // The current sales channel is Shopify.
    isShopify: boolean;
    // The Shopify store is a development/test store.
    isTestStore: boolean;
    // Extra, add-on specific blocking reason, evaluated after the test-store check and before the trial
    // check (e.g. the current plan version does not support the feature). Does not affect notAvailableInTrial.
    extraDisabled?: boolean;
    extraDisabledMessage?: string;
}

// The unit a given add-on is purchased in. Kept as distinct literal strings so existing translations stay intact.
type AddonUnit = 'credits' | 'testOrders';

// Shopify development/test stores cannot purchase. Test orders are blocked on dev stores regardless of
// environment; credit add-ons are only blocked on dev stores while running in production.
const isShopifyTestStoreBlocked = (ctx: AddonAvailabilityContext, unit: AddonUnit): boolean =>
    ctx.isShopify && ctx.isTestStore && (config.isProduction || unit === 'testOrders');

const getTestStoreBlockedMessage = (unit: AddonUnit): string =>
    unit === 'testOrders'
        ? T._('Sorry! Your account is currently a development store, which means you can not purchase test orders.', 'Admin')
        : T._('Sorry! Your account is currently a development store, which means you can not purchase credits.', 'Admin');

const getTrialBlockedMessage = (unit: AddonUnit): string =>
    unit === 'testOrders'
        ? T._('Unable to purchase test orders during the trial period.', 'Admin')
        : T._('Unable to purchase credits during the trial period.', 'Admin');

// Builds the disabled / disabledMessage / notAvailableInTrial fields shared by every add-on.
// Message priority mirrors the original inline logic: test store > extra reason > trial.
const buildAddonAvailability = (ctx: AddonAvailabilityContext, unit: AddonUnit): Partial<ServiceConfig> => {
    const trialBlocked = ctx.inTrial && !ctx.isShopify;
    const testStoreBlocked = isShopifyTestStoreBlocked(ctx, unit);
    const baseBlocked = trialBlocked || testStoreBlocked;

    return {
        disabled: baseBlocked || (ctx.extraDisabled ?? false),
        notAvailableInTrial: baseBlocked,
        disabledMessage: testStoreBlocked
            ? getTestStoreBlockedMessage(unit)
            : ctx.extraDisabled
              ? ctx.extraDisabledMessage
              : ctx.inTrial
                ? getTrialBlockedMessage(unit)
                : undefined
    };
};

// Test Orders add-on: reused by the subscriptions page card and the standalone purchase dialog.
export const getTestOrdersServiceConfig = (availability: AddonAvailabilityContext, overrides?: Partial<ServiceConfig>): ServiceConfig => ({
    serviceName: 'TEST_ORDERS',
    addonKey: 'TEST_ORDERS',
    displayName: T._('Test Orders', 'Admin'),
    description: <>{T._('Purchase additional test orders to validate your customizable products and generate print-ready files.', 'Admin')}</>,
    learnMoreUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360018551700-How-to-test-a-customizable-product-and-place-a-test-order',
    addonCurrencyCode: 'USD',
    addonCtaLabel: T._('Purchase Test Orders', 'Admin'),
    addonPackageUnitLabel: T._('test orders', 'Admin'),
    addonPurchaseSuccessMessage: T._('Your test orders package has been purchased successfully.', 'Admin'),
    ...buildAddonAvailability(availability, 'testOrders'),
    ...overrides
});
